import React, { useState } from "react";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import { ExtratableRepoIcon } from "assets/material-custom-icons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useUserRoleSelector } from "store";
import { IMetaDetails } from "store/types/deal/folder";

import { FileType, RoleName } from "../../../constants";

interface IBulkActionItem {
  id: number;
  entity_type: "file" | "folder" | "form" | "document";
  type?: FileType;
  name?: string;
}

interface Props {
  selectedItems?: (IMetaDetails | IBulkActionItem)[];
  onClose?: () => void;
  onView?: () => void;
  onExtract?: () => void;
  onDownload?: () => void;
  onChangeClassification?: (anchorEl: HTMLElement | null) => void;
  onChangeCurrency?: (anchorEl: HTMLElement | null) => void;
  onChangeInvestor?: (anchorEl: HTMLElement | null) => void;
  onChangeEffectiveDate?: (anchorEl: HTMLElement | null) => void;
  onChangeStatus?: (anchorEl: HTMLElement | null) => void;
  onDelete?: () => void;
}

const iconButtonSx = {
  p: "6px",
  // color: "#496682",
  "&:hover": { backgroundColor: "#587189" }
};

export const BulkActionsBar: React.FC<Props> = ({
  selectedItems = [],
  onClose,
  onView,
  onExtract,
  onDownload,
  onChangeClassification,
  onChangeCurrency,
  onChangeInvestor,
  onChangeEffectiveDate,
  onChangeStatus,
  onDelete
}) => {
  const role = useUserRoleSelector();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const noPermission = role?.name === RoleName.Guest || role?.name === RoleName.Viewer;

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  const handleMenuActionWithAnchor = (action: (anchorEl: HTMLElement | null) => void) => {
    action(anchorEl);
    handleMenuClose();
  };

  if (!selectedItems.length) return null;

  // Check if all selected items are files (not folders)
  const hasFiles = selectedItems.some((item) => item.entity_type === "file");
  const allFiles = selectedItems.every((item) => item.entity_type === "file");

  // Check if selection contains mixed types (files + forms/documents)
  const hasForms = selectedItems.some((item) => item.entity_type === "form");
  const hasDocuments = selectedItems.some((item) => item.entity_type === "document");
  const hasMixedTypes = hasFiles && (hasForms || hasDocuments);

  // Check if files support PDF extraction
  const hasPDFFiles = selectedItems.some((item) => item.entity_type === "file" && item.type === FileType.PDF);

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="12px"
      sx={{
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: "#496682",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        borderRadius: "8px",
        padding: "12px 20px",
        zIndex: 1000
      }}
    >
      {/* Close button */}
      {onClose && (
        <>
          <MaterialTooltip title="Clear selection">
            <IconButton onClick={onClose} sx={{ ...iconButtonSx, color: "white" }}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </MaterialTooltip>
        </>
      )}

      <Typography sx={{ fontSize: 14, color: "white", fontWeight: 500, whiteSpace: "nowrap" }}>
        {selectedItems.length} {selectedItems.length === 1 ? "item" : "items"} selected:
      </Typography>

      <Stack direction="row" alignItems="center" gap="4px">
        {/* Classification - Direct action */}
        {!noPermission && hasFiles && !hasMixedTypes && onChangeClassification && (
          <MaterialTooltip title="Change Classification" isShow>
            <IconButton onClick={(e) => onChangeClassification(e.currentTarget)} sx={iconButtonSx}>
              <LabelOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {/* Status - Direct action */}
        {!noPermission && hasFiles && !hasMixedTypes && onChangeStatus && (
          <MaterialTooltip title="Change Status" isShow>
            <IconButton onClick={(e) => onChangeStatus(e.currentTarget)} sx={iconButtonSx}>
              <FlagOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {/* View - Direct action */}
        {hasPDFFiles && onView && (
          <MaterialTooltip title="View" isShow>
            <IconButton onClick={onView} sx={iconButtonSx}>
              <VisibilityOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {/* Extract Data - Direct action */}
        {!noPermission && hasPDFFiles && !hasMixedTypes && onExtract && (
          <MaterialTooltip title="Extract Data" isShow>
            <IconButton onClick={onExtract} sx={iconButtonSx}>
              <ExtratableRepoIcon sx={{ width: 17, height: 17, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {/* Download - Direct action */}
        {allFiles && onDownload && (
          <MaterialTooltip title="Download" isShow>
            <IconButton onClick={onDownload} sx={iconButtonSx}>
              <FileDownloadOutlinedIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}

        {/* More actions menu */}
        {!noPermission && (
          <MaterialTooltip title="More Actions" isShow>
            <IconButton onClick={handleMenuClick} sx={iconButtonSx}>
              <MoreVertIcon sx={{ fontSize: 20, fill: "white" }} />
            </IconButton>
          </MaterialTooltip>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right"
          }}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "right"
          }}
          slotProps={{
            paper: {
              sx: {
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                borderRadius: "8px",
                minWidth: 220
              }
            }
          }}
        >
          {/* Change Currency */}
          {hasFiles && !hasMixedTypes && onChangeCurrency && (
            <MaterialTooltip title="Change Currency" placement="left">
              <MenuItem onClick={() => handleMenuActionWithAnchor(onChangeCurrency)}>
                <ListItemIcon>
                  <AttachMoneyIcon sx={{ fontSize: 20, color: "#496682" }} />
                </ListItemIcon>
                <ListItemText>Change Currency</ListItemText>
              </MenuItem>
            </MaterialTooltip>
          )}

          {/* Change Investor */}
          {hasFiles && !hasMixedTypes && onChangeInvestor && (
            <MaterialTooltip title="Change Investor" placement="left">
              <MenuItem onClick={() => handleMenuActionWithAnchor(onChangeInvestor)}>
                <ListItemIcon>
                  <PersonOutlineIcon sx={{ fontSize: 20, color: "#496682" }} />
                </ListItemIcon>
                <ListItemText>Change Investor</ListItemText>
              </MenuItem>
            </MaterialTooltip>
          )}

          {/* Change Effective Date */}
          {hasFiles && !hasMixedTypes && onChangeEffectiveDate && (
            <MaterialTooltip title="Change Effective Date" placement="left">
              <MenuItem onClick={() => handleMenuActionWithAnchor(onChangeEffectiveDate)}>
                <ListItemIcon>
                  <EventOutlinedIcon sx={{ fontSize: 20, color: "#496682" }} />
                </ListItemIcon>
                <ListItemText>Change Effective Date</ListItemText>
              </MenuItem>
            </MaterialTooltip>
          )}

          {/* Delete */}
          {onDelete && (
            <MaterialTooltip title="Delete" placement="left">
              <MenuItem onClick={() => handleMenuAction(onDelete)}>
                <ListItemIcon>
                  <DeleteOutlinedIcon sx={{ fontSize: 20, color: "#496682" }} />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </MaterialTooltip>
          )}
        </Menu>
      </Stack>
    </Stack>
  );
};
